import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import moment from 'moment';
import {
  acknowledgeRabbitAlert,
  fetchRabbitAlerts,
  fetchRabbitMessageLog,
  fetchRabbitQueues,
  fetchRabbitSummary,
  retryRabbitMessage,
  triggerDummyWorkerTask,
} from '../../api/rabbitmqDashboard';
import { notify, notifyError } from '../../utils/notify';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const { Option } = Select;
const STATUS_OPTIONS = ['published', 'processing', 'acked', 'nacked', 'failed'];
const ALERT_TYPE_OPTIONS = [
  'queue_stuck_depth',
  'queue_stuck_no_consumers',
  'queue_stuck_processing',
  'queue_message_failed',
];
const DATE_FORMAT = 'YYYY-MM-DD';
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const defaultUtcDay = () => moment.utc().startOf('day');

const formatTime = v => {
  if (v == null || v === '') return '—';
  return moment(v).format('YYYY-MM-DD HH:mm:ss UTC');
};

// log_date is a UTC calendar day (YYYY-MM-DD), not a timestamp.
const formatLogDate = v => {
  if (v == null || v === '') return '';
  if (typeof v === 'string') {
    const ymd = v.slice(0, 10);
    return YMD_RE.test(ymd) ? ymd : '';
  }
  return '';
};

const toUtcLogDate = m => {
  if (!m || !moment.isMoment(m) || !m.isValid()) {
    return moment.utc().format(DATE_FORMAT);
  }
  if (m.isUTC()) {
    return m.format(DATE_FORMAT);
  }
  return moment
    .utc({
      year: m.year(),
      month: m.month(),
      date: m.date(),
    })
    .format(DATE_FORMAT);
};

const utcLogDateToPicker = ymd => (ymd ? moment(ymd, DATE_FORMAT, true) : null);

const payloadPreview = payload => {
  if (payload == null) return '—';
  try {
    const s = JSON.stringify(payload);
    return s.length > 120 ? `${s.slice(0, 120)}…` : s;
  } catch {
    return '—';
  }
};

const extractMessageContextFromAlert = record => {
  const detailsRaw = record && record.details;
  let details = detailsRaw;
  if (typeof detailsRaw === 'string') {
    try {
      details = JSON.parse(detailsRaw);
    } catch {
      details = {};
    }
  }
  if (!details || typeof details !== 'object') {
    details = {};
  }
  const messageId =
    details.message_id || details.messageId || details.failed_message_id || '';
  const logDate = details.log_date || details.logDate || '';
  return {
    messageId: typeof messageId === 'string' ? messageId : '',
    logDate: formatLogDate(logDate),
  };
};

const baseLogColumns = [
  {
    title: 'Published',
    dataIndex: 'publishedAt',
    key: 'publishedAt',
    width: 180,
    render: formatTime,
  },
  {
    title: 'Queue',
    dataIndex: 'queueName',
    key: 'queueName',
    width: 200,
    ellipsis: true,
  },
  {
    title: 'Type',
    dataIndex: 'messageType',
    key: 'messageType',
    width: 160,
    ellipsis: true,
    render: v => (v == null || v === '' ? '—' : v),
  },
  {
    title: 'Host Name',
    dataIndex: 'hostName',
    key: 'hostName',
    width: 180,
    ellipsis: true,
    render: v => (v == null || v === '' ? '—' : v),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    render: v => <Tag>{v}</Tag>,
  },
  {
    title: 'Attempts',
    dataIndex: 'attemptCount',
    key: 'attemptCount',
    width: 90,
  },
  {
    title: 'Message id',
    dataIndex: 'messageId',
    key: 'messageId',
    width: 280,
    ellipsis: true,
  },
  {
    title: 'Log date',
    dataIndex: 'logDate',
    key: 'logDate',
    width: 110,
    render: formatLogDate,
  },
  {
    title: 'Payload',
    dataIndex: 'payload',
    key: 'payload',
    ellipsis: true,
    render: payloadPreview,
  },
];

const SummaryTab = ({ selectedDate }) => {
  const queryClient = useQueryClient();
  const logDate = toUtcLogDate(selectedDate);
  const queuesQuery = useQuery(['rabbitQueues'], () => fetchRabbitQueues());
  const summaryQuery = useQuery(['rabbitSummary', logDate], () =>
    fetchRabbitSummary({ log_date: logDate })
  );

  if (queuesQuery.isLoading || summaryQuery.isLoading) {
    return <Spin size="large" />;
  }

  const { queues = [] } = queuesQuery.data || {};
  const summary = summaryQuery.data || {};
  const byStatus = summary.byStatus || {};
  const byQueueMap = new Map(
    (summary.byQueue || []).map(q => [q.queueName, q])
  );
  const matrixMap = new Map(
    (summary.queueStatusMatrix || []).map(r => [
      `${r.queueName}:${r.status}`,
      r.count,
    ])
  );

  const queueRows = queues.map(q => ({
    key: q.queueName,
    queueName: q.queueName,
    brokerDepth: q.depth != null ? q.depth : '—',
    todayCount: (byQueueMap.get(q.queueName) || {}).count || 0,
    lastActiveAt: (byQueueMap.get(q.queueName) || {}).lastActiveAt || null,
    published: matrixMap.get(`${q.queueName}:published`) || 0,
    processing: matrixMap.get(`${q.queueName}:processing`) || 0,
    acked: matrixMap.get(`${q.queueName}:acked`) || 0,
    nacked: matrixMap.get(`${q.queueName}:nacked`) || 0,
    failed: matrixMap.get(`${q.queueName}:failed`) || 0,
  }));

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card title="Today total">
            <Title level={3} style={{ margin: 0 }}>
              {summary.total || 0}
            </Title>
          </Card>
        </Col>
        <Col span={6}>
          <Card title="Failed">
            <Title level={3} style={{ margin: 0, color: '#cf1322' }}>
              {byStatus.failed || 0}
            </Title>
          </Card>
        </Col>
        <Col span={6}>
          <Card title="Processing">
            <Title level={3} style={{ margin: 0 }}>
              {byStatus.processing || 0}
            </Title>
          </Card>
        </Col>
        <Col span={6}>
          <Card title="Acked">
            <Title level={3} style={{ margin: 0, color: '#389e0d' }}>
              {byStatus.acked || 0}
            </Title>
          </Card>
        </Col>
      </Row>
      <Card
        title={`Today's tally (UTC ${logDate})`}
        extra={
          <Button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['rabbitQueues'] });
              queryClient.invalidateQueries({
                queryKey: ['rabbitSummary'],
              });
            }}
            loading={queuesQuery.isFetching || summaryQuery.isFetching}
          >
            Refresh
          </Button>
        }
      >
        <Table
          rowKey="key"
          dataSource={queueRows}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          columns={[
            {
              title: 'Queue',
              dataIndex: 'queueName',
              key: 'queueName',
              width: 220,
            },
            {
              title: 'Broker depth',
              dataIndex: 'brokerDepth',
              key: 'brokerDepth',
              width: 120,
            },
            {
              title: 'Today count',
              dataIndex: 'todayCount',
              key: 'todayCount',
              width: 110,
            },
            {
              title: 'Last active (UTC)',
              dataIndex: 'lastActiveAt',
              key: 'lastActiveAt',
              width: 180,
              render: formatTime,
            },
            {
              title: 'Published',
              dataIndex: 'published',
              key: 'published',
              width: 100,
            },
            {
              title: 'Processing',
              dataIndex: 'processing',
              key: 'processing',
              width: 100,
            },
            { title: 'Acked', dataIndex: 'acked', key: 'acked', width: 80 },
            { title: 'Nacked', dataIndex: 'nacked', key: 'nacked', width: 90 },
            { title: 'Failed', dataIndex: 'failed', key: 'failed', width: 80 },
          ]}
        />
      </Card>
    </div>
  );
};

const MessageLogTab = ({ filters }) => {
  const { queueName, status, messageType, messageId, selectedDate } = filters;
  const logDate = toUtcLogDate(selectedDate);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const queryClient = useQueryClient();

  const queryParams = {
    queue_name: queueName || undefined,
    status: status || undefined,
    message_type: messageType || undefined,
    message_id: messageId || undefined,
    log_date: logDate,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  };

  const query = useQuery(
    ['rabbitMessageLog', queryParams, page],
    () => fetchRabbitMessageLog(queryParams),
    { keepPreviousData: true }
  );

  const retryMutation = useMutation(body => retryRabbitMessage(body), {
    onSuccess: () => {
      notify('Message republished', 'success');
      queryClient.invalidateQueries(['rabbitMessageLog']);
      queryClient.invalidateQueries(['rabbitSummary']);
      queryClient.invalidateQueries(['rabbitQueues']);
    },
    onError: e => notifyError(e, false, 'Retry failed'),
  });

  const showRetry = status === 'failed' || status === '';
  const columns = [...baseLogColumns];
  if (showRetry) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) =>
        record.status === 'failed' ? (
          <Button
            type="primary"
            size="small"
            loading={retryMutation.isLoading}
            onClick={() => {
              Modal.confirm({
                title: 'Retry this message?',
                content: (
                  <div>
                    <p>
                      Republishes the stored payload to{' '}
                      <strong>{record.queueName}</strong>. The log row will be
                      reset and the message will go through the normal delivery
                      cycle again.
                    </p>
                  </div>
                ),
                okText: 'Retry',
                cancelText: 'Cancel',
                onOk: () =>
                  retryMutation.mutateAsync({
                    message_id: record.messageId,
                    log_date: formatLogDate(record.logDate),
                  }),
              });
            }}
          >
            Retry
          </Button>
        ) : (
          '—'
        ),
    });
  }

  if (query.isLoading && !query.data) {
    return <Spin size="large" />;
  }

  const rows = (query.data && query.data.rows) || [];
  const total = (query.data && query.data.total) || 0;

  return (
    <Table
      rowKey={r => `${r.messageId}-${r.logDate}`}
      dataSource={rows}
      loading={query.isFetching}
      columns={columns}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: false,
        onChange: p => setPage(p),
      }}
      scroll={{ x: true }}
    />
  );
};

const AlertsTab = ({ queueOptions, onOpenMessageFromAlert }) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [queueName, setQueueName] = useState('');
  const [alertType, setAlertType] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [ackModalVisible, setAckModalVisible] = useState(false);
  const [activeAlert, setActiveAlert] = useState(null);
  const [ackMessage, setAckMessage] = useState('');
  const pageSize = 50;

  const sentDate = selectedDate ? toUtcLogDate(selectedDate) : '';
  const queryParams = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    queue_name: queueName || undefined,
    alert_type: alertType || undefined,
    sent_date: sentDate || undefined,
  };
  const query = useQuery(['rabbitAlerts', queryParams], () =>
    fetchRabbitAlerts(queryParams)
  );

  useEffect(() => {
    setPage(1);
  }, [queueName, alertType, sentDate]);

  const acknowledgeMutation = useMutation(
    ({ alertId, message }) => acknowledgeRabbitAlert(alertId, { message }),
    {
      onSuccess: () => {
        notify('Alert acknowledged', 'success');
        setAckModalVisible(false);
        setActiveAlert(null);
        setAckMessage('');
        queryClient.invalidateQueries({ queryKey: ['rabbitAlerts'] });
      },
      onError: e => notifyError(e, false, 'Failed to acknowledge alert'),
    }
  );

  const openAcknowledgeModal = record => {
    setActiveAlert(record);
    setAckMessage('');
    setAckModalVisible(true);
  };

  if (query.isLoading && !query.data) {
    return <Spin size="large" />;
  }

  const rows = (query.data && query.data.rows) || [];
  const total = (query.data && query.data.total) || 0;

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12}>
          <Col span={6}>
            <Text strong>Queue</Text>
            <Select
              value={queueName}
              onChange={v => setQueueName(v)}
              style={{ width: '100%', marginTop: 6 }}
            >
              <Option value="">All</Option>
              {queueOptions.map(q => (
                <Option key={q} value={q}>
                  {q}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Text strong>Type</Text>
            <Select
              value={alertType}
              onChange={v => setAlertType(v)}
              style={{ width: '100%', marginTop: 6 }}
              showSearch
            >
              <Option value="">All</Option>
              {ALERT_TYPE_OPTIONS.map(t => (
                <Option key={t} value={t}>
                  {t}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Text strong>Date (UTC)</Text>
            <DatePicker
              allowClear
              value={
                selectedDate
                  ? utcLogDateToPicker(toUtcLogDate(selectedDate))
                  : null
              }
              onChange={d =>
                setSelectedDate(
                  d
                    ? moment
                        .utc(toUtcLogDate(d), DATE_FORMAT, true)
                        .startOf('day')
                    : null
                )
              }
              format={DATE_FORMAT}
              style={{ width: '100%', marginTop: 6 }}
            />
          </Col>
          <Col span={6} style={{ paddingTop: 27 }}>
            <Button
              onClick={() => {
                setQueueName('');
                setAlertType('');
                setSelectedDate(null);
              }}
            >
              Reset
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              loading={query.isFetching}
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ['rabbitAlerts'] })
              }
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        rowKey="id"
        dataSource={rows}
        loading={query.isFetching}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: p => setPage(p),
        }}
        columns={[
          {
            title: 'Sent at',
            dataIndex: 'sentAt',
            key: 'sentAt',
            width: 180,
            render: formatTime,
          },
          {
            title: 'Queue',
            dataIndex: 'queueName',
            key: 'queueName',
            width: 220,
          },
          { title: 'Depth', dataIndex: 'depth', key: 'depth', width: 90 },
          {
            title: 'Threshold',
            dataIndex: 'thresholdMessages',
            key: 'thresholdMessages',
            width: 90,
          },
          {
            title: 'Stuck minutes',
            dataIndex: 'stuckMinutes',
            key: 'stuckMinutes',
            width: 120,
          },
          { title: 'Region', dataIndex: 'region', key: 'region', width: 90 },
          {
            title: 'Type',
            dataIndex: 'alertType',
            key: 'alertType',
            width: 120,
          },
          {
            title: 'Acknowledged at',
            dataIndex: 'acknowledgeAt',
            key: 'acknowledgeAt',
            width: 180,
            render: formatTime,
          },
          {
            title: 'Acknowledged by',
            dataIndex: 'acknowledgeBy',
            key: 'acknowledgeBy',
            width: 120,
            render: v => (v == null ? '—' : String(v)),
          },
          {
            title: 'Acknowledgement',
            dataIndex: 'message',
            key: 'message',
            width: 220,
            ellipsis: true,
            render: v => (v && v.trim() ? v : '—'),
          },
          {
            title: 'Action',
            key: 'actions',
            width: 220,
            fixed: 'right',
            render: (_, record) => {
              const { messageId, logDate } =
                extractMessageContextFromAlert(record);
              const showViewMessage =
                record.alertType === 'queue_message_failed' &&
                Boolean(messageId);

              return (
                <div style={{ display: 'flex', gap: 8 }}>
                  {showViewMessage && (
                    <Button
                      size="small"
                      onClick={() =>
                        onOpenMessageFromAlert({
                          messageId,
                          queueName: record.queueName || '',
                          logDate,
                          sentAt: record.sentAt || null,
                        })
                      }
                    >
                      View Message
                    </Button>
                  )}
                  {record.acknowledgeAt ? (
                    <Tag color="green">Acknowledged</Tag>
                  ) : (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => openAcknowledgeModal(record)}
                    >
                      Acknowledge
                    </Button>
                  )}
                </div>
              );
            },
          },
        ]}
        scroll={{ x: true }}
      />

      <Modal
        visible={ackModalVisible}
        title="Acknowledge alert"
        okText="Acknowledge"
        cancelText="Cancel"
        confirmLoading={acknowledgeMutation.isLoading}
        onCancel={() => {
          setAckModalVisible(false);
          setActiveAlert(null);
          setAckMessage('');
        }}
        onOk={() => {
          const message = ackMessage.trim();
          if (!message) {
            notify('Please add acknowledgement details', 'warning');
            return;
          }
          acknowledgeMutation.mutate({
            alertId: activeAlert.id,
            message,
          });
        }}
      >
        <Typography.Paragraph type="secondary">
          {activeAlert
            ? `Queue: ${activeAlert.queueName} | Type: ${activeAlert.alertType}`
            : ''}
        </Typography.Paragraph>
        <Text strong>Details</Text>
        <Input.TextArea
          value={ackMessage}
          onChange={e => setAckMessage(e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder="Add acknowledgement details"
          style={{ marginTop: 6 }}
        />
      </Modal>
    </>
  );
};

const RabbitMQQueuesPage = () => {
  const queryClient = useQueryClient();
  const messageLogFetching = useIsFetching({
    queryKey: ['rabbitMessageLog'],
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [queueName, setQueueName] = useState('');
  const [status, setStatus] = useState('');
  const [messageType, setMessageType] = useState('');
  const [messageId, setMessageId] = useState('');
  const [selectedDate, setSelectedDate] = useState(defaultUtcDay);
  const [testWorker, setTestWorker] = useState('worker_queue');
  const [testModalVisible, setTestModalVisible] = useState(false);

  const openMessageFromAlert = ({
    messageId: nextMessageId,
    queueName: nextQueueName,
    logDate,
    sentAt,
  }) => {
    let nextDate = null;
    if (logDate && YMD_RE.test(formatLogDate(logDate))) {
      nextDate = moment.utc(formatLogDate(logDate), DATE_FORMAT, true);
    } else if (sentAt) {
      nextDate = moment.utc(sentAt);
    }
    setActiveTab('messages');
    setQueueName(nextQueueName || '');
    setStatus('');
    setMessageType('');
    setMessageId(nextMessageId || '');
    if (nextDate && nextDate.isValid()) {
      setSelectedDate(nextDate.startOf('day'));
    }
  };

  const query = useQuery(['rabbitQueues'], () => fetchRabbitQueues());
  const typesQuery = useQuery(
    ['rabbitMessageTypeOptions', toUtcLogDate(selectedDate)],
    () =>
      fetchRabbitMessageLog({
        log_date: toUtcLogDate(selectedDate),
        limit: 200,
        offset: 0,
      })
  );

  const queueOptions = useMemo(() => {
    const rows = (query.data && query.data.queues) || [];
    return rows.map(q => q.queueName);
  }, [query.data]);

  const messageTypeOptions = useMemo(() => {
    const rows = (typesQuery.data && typesQuery.data.rows) || [];
    const set = new Set();
    rows.forEach(r => {
      if (r.messageType) set.add(r.messageType);
    });
    return Array.from(set).sort();
  }, [typesQuery.data]);

  const workerOptions = useMemo(
    () => Array.from(new Set(['worker_queue', ...queueOptions])),
    [queueOptions]
  );

  const testWorkerMutation = useMutation(
    params => triggerDummyWorkerTask(params),
    {
      onSuccess: data => {
        notify('DUMMY_TASK sent to worker', 'success');
        setQueueName(data.queueName || testWorker);
        setStatus('');
        setMessageId(data.messageId || '');
        setSelectedDate(defaultUtcDay());
        setActiveTab('messages');
        setTestModalVisible(false);
      },
      onError: e => notifyError(e, false, 'Failed to send DUMMY_TASK'),
    }
  );

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={3}>RabbitMQ Monitoring</Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Monitor queue health and message lifecycle. Filters default to today
            in UTC.
          </Typography.Paragraph>
        </div>
        <Button
          type="primary"
          onClick={() => setTestModalVisible(true)}
          style={{ marginTop: 30 }}
        >
          Test Queue
        </Button>
      </div>
      <Modal
        visible={testModalVisible}
        title="Test Queue"
        okText="Test Queue"
        confirmLoading={testWorkerMutation.isLoading}
        onCancel={() => setTestModalVisible(false)}
        onOk={() =>
          testWorkerMutation.mutate({
            queueName: testWorker,
            seconds: 1,
          })
        }
      >
        <Text strong>Queue Name</Text>
        <Select
          value={testWorker}
          onChange={v => setTestWorker(v)}
          style={{ width: '100%', marginTop: 6 }}
        >
          {workerOptions.map(q => (
            <Option key={q} value={q}>
              {q}
            </Option>
          ))}
        </Select>
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 12, marginBottom: 0 }}
        >
          Sends DUMMY_TASK with a fixed 1-second param and redirects to Messages
          with returned message id filter.
        </Typography.Paragraph>
      </Modal>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Overview" key="overview">
          <SummaryTab selectedDate={defaultUtcDay()} />
        </TabPane>
        <TabPane tab="Messages" key="messages">
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={12}>
              <Col span={6}>
                <Text strong>Queue</Text>
                <Select
                  value={queueName}
                  onChange={v => setQueueName(v)}
                  style={{ width: '100%', marginTop: 6 }}
                >
                  <Option value="">All</Option>
                  {queueOptions.map(q => (
                    <Option key={q} value={q}>
                      {q}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <Text strong>Status</Text>
                <Select
                  value={status}
                  onChange={v => setStatus(v)}
                  style={{ width: '100%', marginTop: 6 }}
                >
                  <Option value="">All</Option>
                  {STATUS_OPTIONS.map(s => (
                    <Option key={s} value={s}>
                      {s}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={5}>
                <Text strong>Type</Text>
                <Select
                  value={messageType}
                  onChange={v => setMessageType(v)}
                  style={{ width: '100%', marginTop: 6 }}
                  showSearch
                >
                  <Option value="">All</Option>
                  {messageTypeOptions.map(t => (
                    <Option key={t} value={t}>
                      {t}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={5}>
                <Text strong>Message id</Text>
                <Input
                  value={messageId}
                  onChange={e => setMessageId(e.target.value)}
                  placeholder="Filter by UUID"
                  style={{ marginTop: 6 }}
                />
              </Col>
              <Col span={4}>
                <Text strong>Date (UTC)</Text>
                <DatePicker
                  allowClear={false}
                  value={utcLogDateToPicker(toUtcLogDate(selectedDate))}
                  onChange={d =>
                    setSelectedDate(
                      d
                        ? moment
                            .utc(toUtcLogDate(d), DATE_FORMAT, true)
                            .startOf('day')
                        : defaultUtcDay()
                    )
                  }
                  format={DATE_FORMAT}
                  style={{ width: '100%', marginTop: 6 }}
                />
              </Col>
              <Col span={4} style={{ paddingTop: 27 }}>
                <Button
                  onClick={() => {
                    setQueueName('');
                    setStatus('');
                    setMessageType('');
                    setMessageId('');
                    setSelectedDate(defaultUtcDay());
                  }}
                >
                  Reset
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  loading={
                    Boolean(messageLogFetching) ||
                    query.isFetching ||
                    typesQuery.isFetching
                  }
                  onClick={() => {
                    queryClient.invalidateQueries({
                      queryKey: ['rabbitMessageLog'],
                    });
                    queryClient.invalidateQueries({
                      queryKey: ['rabbitQueues'],
                    });
                    queryClient.invalidateQueries({
                      queryKey: ['rabbitMessageTypeOptions'],
                    });
                  }}
                >
                  Refresh
                </Button>
              </Col>
            </Row>
          </Card>
          <MessageLogTab
            filters={{
              queueName,
              status,
              messageType,
              messageId,
              selectedDate,
            }}
          />
        </TabPane>
        <TabPane tab="Recent alerts sent" key="alerts">
          <AlertsTab
            queueOptions={queueOptions}
            onOpenMessageFromAlert={openMessageFromAlert}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default RabbitMQQueuesPage;
